
import React from 'react';
import { Ticket, TicketStatus, RepairStageStatus, AppSettings, CommunicationType, Employee, RepairChecklist, AppointmentModalData, InventoryItem } from '../types'; 
import { TicketItem } from './TicketItem';

interface TicketListProps {
  tickets: Ticket[];
  onOpenUpdateStatusModal: (ticketId: string, currentStatus: TicketStatus) => void;
  onViewCustomerProfile?: (customerName: string) => void;
  onOpenPaymentModal: (ticketId: string, amountDue: number, customerName: string) => void;
  onOpenPrintModal: (title: string, content: React.ReactNode, contentClassName?: string) => void;
  onShowCustomerPortal?: (ticketId: string) => void; 
  onAddInternalTicketNote: (ticketId: string, noteText: string, employeeName?: string) => void;
  onUpdateRepairStage: (ticketId: string, stageId: string, newStatus: RepairStageStatus, notes?: string) => void;
  onAddCommunicationLog: (ticketId: string, type: CommunicationType, content: string, recipient?: string, staffName?: string) => void;
  onOpenAppointmentModal: (data?: AppointmentModalData) => void;
  appSettings: AppSettings; 
  currentUser: Employee | null;
  inventoryItems: InventoryItem[];
  onAddPartToTicket: (ticketId: string, inventoryItemId: string, quantityUsed: number, addPriceToTicketTotal: boolean) => void;
  repairChecklists: RepairChecklist[];
  onUpdateTicketChecklistProgress: (ticketId: string, checklistItemId: string, completed: boolean) => void;
}

const InformationCircleIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 mr-2" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
  </svg>
);

export const TicketList: React.FC<TicketListProps> = ({ 
    tickets, 
    onOpenUpdateStatusModal, 
    onViewCustomerProfile, 
    onOpenPaymentModal, 
    onOpenPrintModal,
    onShowCustomerPortal,
    onAddInternalTicketNote,
    onUpdateRepairStage,
    onAddCommunicationLog,
    onOpenAppointmentModal,
    appSettings,
    currentUser,
    inventoryItems,
    onAddPartToTicket,
    repairChecklists,
    onUpdateTicketChecklistProgress,
}) => {
  if (tickets.length === 0) {
    return (
      <div className="bg-white p-8 rounded-lg shadow text-center text-slate-500 flex flex-col items-center">
        <InformationCircleIcon className="text-slate-400 mb-2 w-12 h-12"/>
        <p className="font-semibold">No tickets match the current filter or search.</p>
        <p className="text-sm">Try adjusting your filters/search or creating a new ticket.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {tickets.map(ticket => (
        <TicketItem 
          key={ticket.id} 
          ticket={ticket} 
          onOpenUpdateStatusModal={onOpenUpdateStatusModal}
          onViewCustomerProfile={onViewCustomerProfile}
          onOpenPaymentModal={onOpenPaymentModal}
          onOpenPrintModal={onOpenPrintModal}
          onShowCustomerPortal={onShowCustomerPortal}
          onAddInternalTicketNote={onAddInternalTicketNote}
          onUpdateRepairStage={onUpdateRepairStage}
          onAddCommunicationLog={onAddCommunicationLog}
          onOpenAppointmentModal={onOpenAppointmentModal}
          appSettings={appSettings} 
          currentUser={currentUser}
          inventoryItems={inventoryItems}
          onAddPartToTicket={onAddPartToTicket}
          repairChecklists={repairChecklists}
          onUpdateTicketChecklistProgress={onUpdateTicketChecklistProgress}
        />
      ))}
    </div>
  );
};
